/**
 * Created on Fri Jun 12 2026 4:08:23 PM
 *
 * *************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2026
 * All Rights Reserved
 *
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ************************************************************************************/


import { computeScaleFactor, scaleLayout } from './layoutCore';

const isSectionLikeNode = (node) => {
  if (!node || node.nodeType !== 1) return false;
  if (node.hasAttribute && node.hasAttribute('data-reader-section')) return true;
  return String(node.tagName || '').toLowerCase() === 'section';
};

const forceFullWidthSectionNode = (node, height) => {
  if (!node || !node.style) return;
  node.style.width = '100%';
  node.style.maxWidth = 'none';
  node.style.marginLeft = '0';
  node.style.marginRight = '0';
  node.style.transform = 'none';
  if (Number.isFinite(Number(height)) && Number(height) > 0) {
    node.style.minHeight = `${Math.round(Number(height))}px`;
    node.style.height = '';
  }
};

export function computeAppliedBox(layout, { viewportWidth, referenceWidth }) {
  // Absolute-positioned elements (the canvas-builder model) scale UNIFORMLY by the exact
  // viewport/reference ratio — uncapped — so the published page reproduces the design at
  // the SAME proportions the builder showed (which renders at the reference width and
  // CSS-scales the whole stage). Capping at 1.0 (the legacy behavior) left elements
  // un-scaled on screens wider than the reference width, drifting from their placement.
  // Legacy flow-relative entries keep the original capped scale factor.
  const absolute = layout && layout.geometryAbsolute === true;
  let scaleFactor;
  if (absolute && Number.isFinite(viewportWidth) && Number.isFinite(referenceWidth) && referenceWidth > 0) {
    scaleFactor = Math.min(Math.max(viewportWidth / referenceWidth, 0.1), 6);
  } else {
    scaleFactor = computeScaleFactor(viewportWidth, referenceWidth);
  }
  const scaled = scaleLayout(layout, scaleFactor);

  return {
    left: scaled.x,
    top: scaled.y,
    width: scaled.width,
    height: scaled.height,
    zIndex: scaled.z,
    scaleFactor,
    absolute,
  };
}

export function applyLayout(matchedPairs, { viewportWidth, referenceWidth }) {
  if (!Array.isArray(matchedPairs)) return [];

  const applied = [];
  // Entries that need ABSOLUTE positioning are resolved in a SECOND pass, after every
  // element's size/style has been applied. Positioning an element depends on measuring
  // its natural offset within the section, and that natural offset shifts when a sibling
  // in the same section changes size. Doing all sizing first (phase 1) then all measuring
  // + translating (phase 2) makes placement order-independent and pixel-accurate for ANY
  // number of moved elements in a section — and collapses layout reads to a single reflow.
  const deferredAbsolute = [];

  // ── Phase 1: hidden / size / style / content / non-absolute translate ──────────────
  for (let i = 0; i < matchedPairs.length; i += 1) {
    const pair = matchedPairs[i];
    if (!pair || typeof pair !== 'object') continue;

    const { elementId, node, layout } = pair;
    const box = computeAppliedBox(layout, { viewportWidth, referenceWidth });

    if (layout && layout.hidden === true) {
      if (node && node.style) {
        node.style.display = 'none';
      }
      applied.push({ elementId, hidden: true });
      continue;
    }

    if (node && node.style) {
      // Apply geometry ONLY when the author actually moved/resized the element. Pure
      // style/content edits (color/text/font/image) leave the element in its natural
      // responsive flow, so they never reposition or overlap. (Absent flag = old save =
      // geometry-edited, preserving prior behavior.)
      const applyGeometry = !layout || layout.geometryEdited !== false;
      const sectionLike = isSectionLikeNode(node);
      if (applyGeometry) {
        node.style.boxSizing = 'border-box';
        node.style.position = 'relative';
        if (sectionLike) {
          forceFullWidthSectionNode(node, box.height);
          node.style.zIndex = String(box.zIndex);
        } else {
          try {
            const view = node.ownerDocument && node.ownerDocument.defaultView;
            if (view && view.getComputedStyle(node).display === 'inline') {
              node.style.display = 'inline-block';
            }
          } catch (error) { /* getComputedStyle unavailable */ }
          node.style.width = `${box.width}px`;
          node.style.height = `${box.height}px`;
          node.style.zIndex = String(box.zIndex);
          if (layout && layout.geometryAbsolute === true) {
            // Clear any prior translate so phase 2 measures the TRUE natural offset, then
            // defer the absolute placement until all sizes are settled.
            node.style.transform = 'none';
            deferredAbsolute.push({ node, box });
          } else {
            // Legacy entries: x/y are a flow-relative delta (preserve original behavior).
            node.style.transform = `translate(${box.left}px, ${box.top}px)`;
          }
        }
      }
      if (sectionLike) {
        forceFullWidthSectionNode(node, box.height);
      }
      if (layout && typeof layout.backgroundColor === 'string') {
        node.style.backgroundColor = layout.backgroundColor;
      }
      if (layout && typeof layout.backgroundImage === 'string' && layout.backgroundImage.trim()) {
        node.style.backgroundImage = `url(${layout.backgroundImage.trim()})`;
        node.style.backgroundSize = 'cover';
        node.style.backgroundPosition = 'center';
        node.style.backgroundRepeat = 'no-repeat';
      }
      if (layout && typeof layout.textColor === 'string') {
        node.style.color = layout.textColor;
        // Force text color on all child text elements (cards have children with own styles)
        node.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,a,button,label,li,td,th,blockquote,div').forEach((child) => {
          child.style.color = layout.textColor;
        });
      }
      if (layout && typeof layout.fontFamily === 'string') {
        node.style.fontFamily = layout.fontFamily;
        node.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,a,button,label,li,td,th,blockquote,div').forEach((child) => {
          child.style.fontFamily = layout.fontFamily;
        });
      }
      if (layout && typeof layout.fontSize === 'number') {
        // Scale font uniformly with the element for absolute entries so text matches the
        // design proportions; legacy entries keep the raw size.
        const fs = box.absolute ? layout.fontSize * box.scaleFactor : layout.fontSize;
        node.style.fontSize = `${fs}px`;
        // Force font size on child elements so cards render correctly
        node.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,a,button,label,li,td,th,blockquote,div').forEach((child) => {
          child.style.fontSize = `${fs}px`;
        });
      }
      if (layout && layout.fontWeight !== undefined && layout.fontWeight !== null && layout.fontWeight !== '') {
        node.style.fontWeight = String(layout.fontWeight);
        node.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,a,button,label,li,td,th,blockquote,div').forEach((child) => {
          child.style.fontWeight = String(layout.fontWeight);
        });
      }
      if (layout && typeof layout.textAlign === 'string') {
        node.style.textAlign = layout.textAlign;
        node.querySelectorAll('h1,h2,h3,h4,h5,h6,p,span,a,button,label,li,td,th,blockquote,div').forEach((child) => {
          child.style.textAlign = layout.textAlign;
        });
      }
      if (layout && typeof layout.borderRadius === 'number' && Number.isFinite(layout.borderRadius) && layout.borderRadius >= 0) {
        // Scale corner radius uniformly with the element for absolute entries so corners
        // match the design; legacy entries keep the raw radius.
        const br = box.absolute ? layout.borderRadius * box.scaleFactor : layout.borderRadius;
        node.style.borderRadius = `${br}px`;
      }
      if (layout && typeof layout.src === 'string' && node.tagName === 'IMG') {
        if (node.getAttribute('src') !== layout.src) node.setAttribute('src', layout.src);
      }
      // Only set text on a LEAF element (no element children) so a text edit never
      // destroys nested markup and shifts other elements' computed ids.
      if (layout && typeof layout.text === 'string' && node.tagName !== 'IMG' && node.childElementCount === 0) {
        if (node.textContent !== layout.text) node.textContent = layout.text;
      }
    }

    applied.push({
      elementId,
      left: box.left,
      top: box.top,
      width: box.width,
      height: box.height,
      zIndex: box.zIndex,
    });
  }

  // ── Phase 2: absolute placement (measure natural within section, translate to target) ─
  for (let i = 0; i < deferredAbsolute.length; i += 1) {
    const { node, box } = deferredAbsolute[i];
    let tx = box.left;
    let ty = box.top;
    const sectionEl = typeof node.closest === 'function' ? node.closest('[data-reader-section]') : null;
    if (sectionEl && typeof sectionEl.getBoundingClientRect === 'function' && typeof node.getBoundingClientRect === 'function') {
      // An ancestor "stage" may uniformly CSS-scale the whole section on the published
      // page. getBoundingClientRect is POST-scale, while offsetWidth is the unscaled
      // layout width — their ratio is the active scale. Convert the measured natural
      // offset back into the element's own (unscaled) coordinate space so the translate,
      // which is applied in that same unscaled space, lands the element exactly. With no
      // stage the ratio is 1 and this reduces to a plain measurement.
      const nrect = node.getBoundingClientRect();
      const ow = node.offsetWidth;
      const s = (ow > 0 && nrect.width > 0) ? (nrect.width / ow) : 1;
      const srect = sectionEl.getBoundingClientRect();
      const naturalX = (nrect.left - srect.left) / s;
      const naturalY = (nrect.top - srect.top) / s;
      tx = Math.round(box.left - naturalX);
      ty = Math.round(box.top - naturalY);
    }
    node.style.transform = `translate(${tx}px, ${ty}px)`;
  }

  return applied;
}

export default applyLayout;
